import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';
import { absoluteImageUrl } from '@/components/admin/ImageUploader';
import { ENTRY_TYPES, formatDate, labelFor } from '@/lib/taxonomies';
import { useSeo } from '@/lib/seo';
import { SEASON } from '@/constants/testIds';

export default function SeasonListPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const type = params.get('type') || 'all';

  useSeo({
    title: 'The Season | A Guy Who Hunts',
    description:
      'A chronological hunting journal: trail cameras, public-land scouting, small-property observations, hunts, mistakes, and lessons from one honest season.',
  });

  useEffect(() => {
    setLoading(true);
    publicApi
      .seasonList(type === 'all' ? {} : { entry_type: type })
      .then(setEntries)
      .finally(() => setLoading(false));
  }, [type]);

  const filters = useMemo(() => [{ value: 'all', label: 'All' }, ...ENTRY_TYPES], []);

  return (
    <div>
      <section className="relative border-b border-[hsl(var(--border))] hero-radial">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <p className="kicker">The season</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 leading-tight">
            A documentary timeline of one honest whitetail season.
          </h1>
          <p className="mt-4 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Trail cameras. Public-land scouting. Small-property observations. Hunts. Weather. Mistakes. Lessons. Every
            entry, in order.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="-mx-1 overflow-x-auto pb-2">
          <div className="flex items-center gap-2 px-1">
            {filters.map((f) => {
              const active = type === f.value;
              return (
                <button
                  key={f.value}
                  onClick={() => setParams(f.value === 'all' ? {} : { type: f.value })}
                  data-testid={`${SEASON.filterButton}-${f.value}`}
                  className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent'
                      : 'bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))] hover:text-[hsl(var(--foreground))]'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-20">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-72 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-10 text-center">
            <p className="font-serif text-2xl">Nothing here yet.</p>
            <p className="mt-2 text-[hsl(var(--muted-foreground))]">Come back when the next entry drops.</p>
            <Link to="/newsletter">
              <Button className="mt-5">Get updates by email</Button>
            </Link>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {entries.map((e) => (
              <Link key={e.slug} to={`/season/${e.slug}`} data-testid={SEASON.card} className="group">
                <Card className="h-full overflow-hidden border-[hsl(var(--border))] card-elev card-elev-hover">
                  <AspectRatio ratio={4 / 3}>
                    <img
                      src={absoluteImageUrl(e.hero_image?.url)}
                      alt={e.hero_image?.alt || e.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </AspectRatio>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                      <Badge variant="secondary" className="font-normal">
                        {labelFor(ENTRY_TYPES, e.entry_type)}
                      </Badge>
                      <span>•</span>
                      <time>{formatDate(e.date)}</time>
                    </div>
                    <h3 className="font-serif text-xl leading-snug mt-2 tracking-tight">{e.title}</h3>
                    <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">{e.summary}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </motion.div>
        )}
      </section>
    </div>
  );
}
